/**
 * AuthFlowPage — unified auth flow page for forgot-password & reset-password.
 *
 * KEY DESIGN: Uses LoginTemplateRenderer (the SAME renderer as /login) so that
 * the left panel, gradient, branding, logo, card position, and footer are
 * IDENTICAL to the login page. Only the card content differs.
 *
 * Mode detection:
 *   - Route contains "reset-password" OR has ?token= → reset mode
 *   - Otherwise → forgot mode
 */
import React, { useState, useEffect, useRef, useMemo, FormEvent } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProgramThemeSettings } from '../contexts/ProgramThemeContext';
import { useAppThemeSettings } from '../contexts/AppThemeContext';
import { legalPageService } from '../services/legalPageService';
import { loginPageConfigService } from '../services/loginPageConfigService';
import { ButtonWithIcon, IconGhostButton } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { authService } from '../services/authService';
import { appRoutes } from '../constants/routes';
import LoginTemplateRenderer from '../components/login/LoginTemplateRenderer';
import { AppInlineLoader, AppPageSkeleton } from '../components/ui/AppLoading';
import type { LoginTemplateConfig, LoginTemplateProps } from '../types/loginTemplate';
import { ArrowLeft, Eye, EyeOff, KeyRound, LogIn, RefreshCw, RotateCcw, Send } from 'lucide-react';

// ─── Icon components ───────────────────────────────────────────
const LockIcon = () => (
  <svg className="h-14 w-14 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="h-14 w-14 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon = () => (
  <svg className="h-14 w-14 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function AuthFlowPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { programCode: urlProgramCode } = useParams<{ programCode?: string }>();
  const themeSettings = useProgramThemeSettings();
  const appThemeSettings = useAppThemeSettings();

  // Detect mode from route
  const isResetRoute = location.pathname.includes('reset-password') || !!searchParams.get('token');

  // ─── Fetch the SAME login page config as /login ──────────────
  const { data: loginConfigResponse, isLoading: configLoading } = useQuery({
    queryKey: ['loginPageConfig', urlProgramCode],
    queryFn: () => loginPageConfigService.getResolvedConfig(urlProgramCode),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: loginLinksResponse } = useQuery({
    queryKey: ['legalPages', 'login-links'],
    queryFn: () => legalPageService.getLoginLinks(),
  });
  const loginLinks = loginLinksResponse?.data || [];

  const isProgramLogin = !!urlProgramCode || themeSettings.hasCustomSettings;

  // ─── Build template config (identical to LoginPage) ──────────
  const templateConfig: LoginTemplateConfig = useMemo(() => {
    const apiConfig = loginConfigResponse?.config;
    if (apiConfig) {
      return {
        templateId: apiConfig.templateId,
        templateName: '',
        description: '',
        formPlacement: apiConfig.formPlacement,
        sections: apiConfig.sections,
        layout: apiConfig.layout ? {
          showLeftPanel: apiConfig.layout.showLeftPanel,
          showRightPanel: apiConfig.layout.showRightPanel,
        } : undefined,
        logoPosition: apiConfig.logoPosition,
        background: {
          type: (apiConfig.backgroundType as 'gradient' | 'solid' | 'image') || 'gradient',
          value: apiConfig.backgroundValue || undefined,
          patternEnabled: apiConfig.backgroundPatternEnabled ?? true,
          patternType: apiConfig.backgroundPatternType || 'dots',
          patternOpacity: apiConfig.backgroundPatternOpacity ?? 10,
          gradientColor1: apiConfig.backgroundGradientColor1,
          gradientColor2: apiConfig.backgroundGradientColor2,
          gradientDirection: apiConfig.gradientDirection,
        },
        colors: {
          heading: apiConfig.textColorHeading,
          subheading: apiConfig.textColorSubheading,
          description: apiConfig.textColorDescription,
          featureTitle: apiConfig.textColorFeatureTitle,
          featureDescription: apiConfig.textColorFeatureDescription,
          statsLabel: apiConfig.textColorStatsLabel,
          statsValue: apiConfig.textColorStatsValue,
          featureCardBox: apiConfig.boxColorFeatureCard,
          statsContainerBox: apiConfig.boxColorStatsContainer,
          loginFormBox: apiConfig.boxColorLoginForm,
        },
        styles: apiConfig.styles ? {
          leftPanel: apiConfig.styles.leftPanel,
          rightPanel: apiConfig.styles.rightPanel,
          header: apiConfig.styles.header ? { color: apiConfig.styles.header.textColor } : undefined,
          cards: apiConfig.styles.cards,
          button: apiConfig.styles.button,
          loginFormGlass: apiConfig.styles.loginFormGlass,
        } : undefined,
        customLogoUrl: apiConfig.customLogoUrl,
        customTitle: apiConfig.customTitle,
        customSubtitle: apiConfig.customSubtitle,
        customDescription: apiConfig.customDescription,
        customFeatureCards: apiConfig.featureCards,
        customStats: apiConfig.stats,
      };
    }
    // Fallback – same default as LoginPage
    return {
      templateId: 'classic' as const,
      templateName: 'Classic Split',
      description: 'Default login template',
      formPlacement: 'right' as const,
      sections: [
        { id: 'logo' as const, visible: true, order: 1 },
        { id: 'welcome' as const, visible: true, order: 2 },
        { id: 'intro' as const, visible: true, order: 3 },
        { id: 'featureCards' as const, visible: true, order: 4 },
        { id: 'stats' as const, visible: true, order: 5 },
        { id: 'help' as const, visible: true, order: 7 },
        { id: 'legalLinks' as const, visible: true, order: 8 },
        { id: 'backgroundVisual' as const, visible: true, order: 9 },
      ],
      background: {
        type: 'gradient' as const,
        patternEnabled: true,
      },
    };
  }, [loginConfigResponse]);

  // ─── Loading state ───────────────────────────────────────────
  if (configLoading) {
    return (
      <div className="min-h-[100dvh] w-full bg-gradient-to-br from-primary-soft via-background to-secondary-soft p-4">
        <AppPageSkeleton showFilters={false} showSearch={false} columns={2} rows={5} />
      </div>
    );
  }

  // ─── Build the form element ──────────────────────────────────
  const formElement = isResetRoute ? (
    <div className="p-4">
      <ResetForm token={searchParams.get('token') || ''} programCode={urlProgramCode} />
    </div>
  ) : (
    <div className="p-4">
      <ForgotForm programCode={urlProgramCode} />
    </div>
  );

  // ─── Render using the SAME template renderer as /login ───────
  return (
    <LoginTemplateRenderer
      config={templateConfig}
      loginForm={formElement}
      themeSettings={themeSettings as unknown as LoginTemplateProps['themeSettings']}
      appThemeSettings={appThemeSettings as unknown as LoginTemplateProps['appThemeSettings']}
      isProgramLogin={isProgramLogin}
      urlProgramCode={urlProgramCode}
      legalLinks={loginLinks.map(l => ({ ...l, externalUrl: l.externalUrl ?? undefined }))}
    />
  );
}

// ═══════════════════════════════════════════════════════════════
// ANIMATED PANEL WRAPPER
// ═══════════════════════════════════════════════════════════════
function AnimatedPanel({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-300 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FORGOT PASSWORD FORM
// ═══════════════════════════════════════════════════════════════
function ForgotForm({ programCode }: { programCode?: string }) {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const loginPath = programCode ? `/p/${programCode}/login` : '/login';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Username or email is required');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const result = await authService.forgotPassword(username);

      if (result.success && result.data?.emailSent) {
        setEmailSent(true);
      } else if (!result.success) {
        setError(result.message || 'Failed to send reset email. Please try again.');
      } else {
        setEmailSent(true);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Success state ───────────────────────────────────────────
  if (emailSent) {
    return (
      <AnimatedPanel key="forgot-success">
        <div className="space-y-5">
          <div className="text-center">
            <h2 className="text-base font-semibold text-foreground">Check Your Email</h2>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            If an account exists with the username <strong>{username}</strong>, we've sent a password reset link.
            The link will expire in 1 hour.
          </p>
          <div className="space-y-3">
            <ButtonWithIcon
              onClick={() => { setEmailSent(false); setUsername(''); }}
              variant="outline"
              className="w-full h-9 text-sm"
              icon={RotateCcw}
            >
              Try Another Username
            </ButtonWithIcon>
            <Link to={loginPath} className="block w-full">
              <ButtonWithIcon className="w-full h-9 text-sm" icon={ArrowLeft}>
                Back to Login
              </ButtonWithIcon>
            </Link>
          </div>
        </div>
      </AnimatedPanel>
    );
  }

  // ─── Form state ──────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-base font-semibold text-foreground">Forgot Password?</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Enter your username and we'll send you a reset link
        </p>
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-destructive text-center">{error}</p>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="username" className="text-xs font-medium text-muted-foreground">
            Username
          </label>
          <Input
            id="username"
            type="text"
            placeholder="Enter your username or email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={loading}
            className={`h-9 text-sm ${error ? 'border-destructive' : ''}`}
            autoComplete="username"
            autoFocus
          />
        </div>

        <ButtonWithIcon
          type="submit"
          disabled={loading || !username.trim()}
          className="w-full h-9 text-sm font-medium"
          icon={Send}
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </ButtonWithIcon>
      </form>

      {/* Secondary actions */}
      <div className="text-center space-y-2">
        <p className="text-xs text-muted-foreground">
          Didn't receive an email?{' '}
          <button
            type="button"
            onClick={() => { setError(''); setUsername(''); }}
            className="text-primary hover:underline font-medium"
          >
            Try again
          </button>
        </p>
        <Link
          to={loginPath}
          className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Login
        </Link>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RESET PASSWORD FORM
// ═══════════════════════════════════════════════════════════════
function ResetForm({ token, programCode }: { token: string; programCode?: string }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const loginPath = programCode ? `/p/${programCode}/login` : '/login';

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid reset link. No token provided.');
        setValidating(false);
        return;
      }

      try {
        const result = await authService.validateResetToken(token);

        if (result.success && result.data?.isValid) {
          setTokenValid(true);
          setTokenEmail(result.data.email || null);
        } else {
          setError(result.data?.message || result.message || 'Invalid or expired reset link.');
        }
      } catch {
        setError('Unable to validate reset link. Please try again.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Password validation
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character');
    return errors;
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    setPasswordErrors(validatePassword(value));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setError('Password does not meet requirements.');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.resetPassword(token, newPassword, confirmPassword);

      if (result.success && result.data?.success) {
        setResetSuccess(true);
      } else {
        setError(result.data?.message || result.message || 'Failed to reset password. Please try again.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Loading state ───────────────────────────────────────────
  if (validating) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <AppInlineLoader label="Loading content" />
        <p className="text-xs text-muted-foreground">Validating reset link...</p>
      </div>
    );
  }

  // ─── Invalid token state ─────────────────────────────────────
  if (!tokenValid && !validating) {
    return (
      <AnimatedPanel key="reset-invalid">
        <div className="space-y-5">
          <div className="text-center">
            <div className="flex justify-center mb-3"><XCircleIcon /></div>
            <h2 className="text-base font-semibold text-foreground">Invalid Reset Link</h2>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
          </div>
          <div className="space-y-3">
            <Link to={appRoutes.forgotPassword()} className="block w-full">
              <ButtonWithIcon variant="outline" className="w-full h-9 text-sm" icon={RefreshCw}>
                Request New Reset Link
              </ButtonWithIcon>
            </Link>
            <Link to={loginPath} className="block w-full">
              <ButtonWithIcon className="w-full h-9 text-sm" icon={ArrowLeft}>
                Back to Login
              </ButtonWithIcon>
            </Link>
          </div>
        </div>
      </AnimatedPanel>
    );
  }

  // ─── Success state ───────────────────────────────────────────
  if (resetSuccess) {
    return (
      <AnimatedPanel key="reset-success">
        <div className="space-y-5">
          <div className="text-center">
            <div className="flex justify-center mb-3"><CheckCircleIcon /></div>
            <h2 className="text-base font-semibold text-foreground">Password Reset Successful!</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
          </div>
          <Link to={loginPath} className="block w-full">
            <ButtonWithIcon className="w-full h-9 text-sm" icon={LogIn}>
              Go to Login
            </ButtonWithIcon>
          </Link>
        </div>
      </AnimatedPanel>
    );
  }

  // ─── Reset password form ─────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-3"><LockIcon /></div>
        <h2 className="text-base font-semibold text-foreground">Reset Your Password</h2>
        {tokenEmail && (
          <p className="text-xs text-muted-foreground mt-1">for {tokenEmail}</p>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="text-xs font-medium text-muted-foreground block mb-1">
            New Password
          </label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              required
              disabled={loading}
              className="h-9 text-sm pr-10"
              autoComplete="new-password"
            />
            <IconGhostButton
              type="button"
              icon={showNewPassword ? EyeOff : Eye}
              tooltip={showNewPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowNewPassword(!showNewPassword)}
              disabled={loading}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            />
          </div>

          {/* Password requirements */}
          {newPassword && (
            <div className="mt-2 text-[11px]">
              <p className="font-medium text-muted-foreground mb-1">Password must have:</p>
              <ul className="space-y-0.5">
                {[
                  { check: newPassword.length >= 8, label: 'At least 8 characters' },
                  { check: /[A-Z]/.test(newPassword), label: 'One uppercase letter' },
                  { check: /[a-z]/.test(newPassword), label: 'One lowercase letter' },
                  { check: /[0-9]/.test(newPassword), label: 'One number' },
                  { check: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword), label: 'One special character' },
                ].map((req, i) => (
                  <li key={i} className={`flex items-center gap-1 ${req.check ? 'text-success' : 'text-muted-foreground'}`}>
                    {req.check ? (
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    {req.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="text-xs font-medium text-muted-foreground block mb-1">
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              className={`h-9 text-sm pr-10 ${
                confirmPassword && newPassword !== confirmPassword
                  ? 'border-destructive focus:ring-destructive focus:border-destructive'
                  : ''
              }`}
              autoComplete="new-password"
            />
            <IconGhostButton
              type="button"
              icon={showConfirmPassword ? EyeOff : Eye}
              tooltip={showConfirmPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            />
          </div>
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="mt-1 text-[11px] text-destructive">Passwords do not match</p>
          )}
        </div>

        <ButtonWithIcon
          type="submit"
          disabled={loading || passwordErrors.length > 0 || newPassword !== confirmPassword || !newPassword || !confirmPassword}
          className="w-full h-9 text-sm font-medium"
          icon={KeyRound}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </ButtonWithIcon>
      </form>

      {/* Secondary actions */}
      <div className="text-center space-y-2">
        <p className="text-xs text-muted-foreground">
          Make sure your password is strong and secure
        </p>
        <Link
          to={loginPath}
          className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Login
        </Link>
      </div>
    </div>
  );
}
